'use client';
import { Hit as AlgoliaHit } from 'instantsearch.js/es/types';
import { AppImage, Icon, Typography, typographyCva } from '@workspace/ui/common';
import { Highlight } from 'react-instantsearch';
import { SearchAllHit } from '@workspace/interfaces';
import { handleLinkClick } from '@workspace/hooks';
import Link from 'next/link';
import { cn } from '@workspace/lib';

interface HeaderSearchHitProps {
  hit: AlgoliaHit<Partial<SearchAllHit>>;
  className?: string;
  onClick?: (hit: AlgoliaHit) => void;
  section?: 'products' | 'documents' | 'articles';
}

export const isPdfUrl = (url: string) => /\.pdf(?:[?#].*)?$/i.test(url);

export function HeaderSearchHit({ hit, className, onClick, section }: HeaderSearchHitProps) {
  const { title, fileUrl, thumbnailImage: image, _type } = hit;
  const description = hit.description || hit.productSummary || hit.content;

  const onClickHandler = (e: React.MouseEvent) => {
    onClick?.(hit);
    if (hit._type === 'documentFile' && hit.fileUrl) {
      e.preventDefault();
      handleLinkClick(
        {
          type: 'external',
          url: fileUrl,
          label: 'View',
        },
        e,
      );
    }
  };

  return (
    <Wrapper
      href={hit.url || hit.fileUrl}
      onClick={onClickHandler}
      className={cn(
        'flex items-center bg-white rounded-md transition-colors border border-secondary-100 min-w-80 md:min-w-auto h-30.5 md:h-auto',
        section === 'documents' || section === 'articles' ? 'w-81.75 md:w-auto' : 'w-auto',
        hit.url || hit.fileUrl ? 'hover:bg-secondary-50 cursor-pointer' : 'cursor-not-allowed',
        className,
      )}
    >
      <div
        className={cn(
          'relative w-[6.5625rem] h-[7.5rem] flex-shrink-0 bg-light-silver rounded-l-md',
          _type === 'article' && 'md:w-[5.4375rem] md:h-26',
        )}
      >
        {image ? (
          <AppImage src={image} alt={title || 'Image'} className="object-cover rounded-l-md" fill sizes="105px" />
        ) : _type === 'documentFile' ? (
          <div className="size-full bg-white flex items-center justify-center rounded-l-md">
            <div className="flex items-center justify-center w-16 h-22.5 bg-secondary-50">
              <Icon
                name="FileBend"
                size={32}
                color="var(--color-raisin-black)"
                className="pointer-events-none select-none"
              />
            </div>
          </div>
        ) : null}
      </div>
      <div
        className={cn(
          'flex-1 space-y-1 px-2 py-0',
          section === 'products' && 'md:pl-4 md:pr-8',
          section === 'documents' && 'md:py-4 md:pl-3 md:pr-4',
          section === 'articles' && 'justify-start md:py-3 md:pl-3 md:pr-4 md:h-26',
        )}
      >
        <Typography
          variant="bodyMd"
          className={cn(
            'line-clamp-2 font-bold !text-sm md:!text-base',
            _type === 'article' && 'line-clamp-3 font-bold md:font-semibold !text-sm md:!text-base',
          )}
        >
          {title || 'No title available'}
        </Typography>
        {_type !== 'article' && _type !== 'documentFile' && description && (
          <Typography variant="bodySm" className="line-clamp-2 !text-xs md:!text-sm text-secondary-500">
            {description}
          </Typography>
        )}
      </div>
    </Wrapper>
  );
}

const Wrapper = ({
  children,
  href,
  onClick,
  className,
}: {
  children: React.ReactNode;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
}) => {
  if (href) {
    return (
      <Link href={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }

  return <div className={className}>{children}</div>;
};

export function HeaderQuerySuggestionHit({ hit, onClick }: { hit: AlgoliaHit; onClick?: (hit: AlgoliaHit) => void }) {
  return (
    <button className={cn('cursor-pointer hover:bg-secondary-50')} onClick={() => onClick?.(hit)}>
      <Highlight
        attribute="query"
        hit={hit}
        classNames={{
          root: cn(typographyCva({ variant: 'bodyMd' }), '!text-sm md:!text-base'),
          highlighted: 'font-bold bg-transparent',
        }}
      />
    </button>
  );
}
